"use client"

import { Fragment, useEffect, useRef } from "react"
import type { ReactNode } from "react"

import { Menu as HUIMenu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"

import { tv } from "@/lib/utils/tv"

const menu = tv({
  slots: {
    root: "relative inline-flex",
    items:
      "z-popover rounded-6x border-line-subtle bg-surface-raised shadow-floating ease-productive duration-quick absolute top-full mt-200 w-56 border p-100 transition data-closed:scale-95 data-closed:opacity-0 focus:outline-none",
    item: "rounded-4x font-text text-body flex w-full items-center gap-200 px-300 py-200 text-left transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50",
  },
  variants: {
    tone: {
      default: { item: "text-fg data-focus:bg-surface-subtle" },
      danger: { item: "text-fg-danger data-focus:bg-error-fill" },
    },
    align: {
      start: { items: "left-0 origin-top-left" },
      end: { items: "right-0 origin-top-right" },
    },
  },
  defaultVariants: { tone: "default", align: "start" },
})

export interface MenuItemSpec {
  label: ReactNode
  onSelect: () => void
  icon?: ReactNode
  tone?: "default" | "danger"
  disabled?: boolean
}

interface MenuProps {
  trigger: ReactNode
  items: MenuItemSpec[]
  align?: "start" | "end"
  onOpenChange?: (open: boolean) => void
}

const OpenReporter = ({ open, onOpenChange }: { open: boolean; onOpenChange?: (open: boolean) => void }) => {
  const reported = useRef(open)
  useEffect(() => {
    if (reported.current === open) return
    reported.current = open
    onOpenChange?.(open)
  }, [open, onOpenChange])
  return null
}

/* CSS-positioned on purpose: headlessui's `anchor` (floating-ui) infinite-loops
   on Fragment triggers ("Maximum update depth exceeded"). Don't add it back. */
export const Menu = ({ trigger, items, align, onOpenChange }: MenuProps) => {
  const styles = menu({ align })
  return (
    <HUIMenu as="div" className={styles.root()}>
      {({ open }) => (
        <>
          <OpenReporter open={open} onOpenChange={onOpenChange} />
          <MenuButton as={Fragment}>{trigger}</MenuButton>
          <MenuItems transition className={styles.items()}>
            {items.map((item, index) => (
              <MenuItem key={index} disabled={item.disabled}>
                <button type="button" onClick={item.onSelect} className={menu({ tone: item.tone ?? "default" }).item()}>
                  {item.icon && <span className="text-fg-subtle shrink-0">{item.icon}</span>}
                  {item.label}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </>
      )}
    </HUIMenu>
  )
}
